import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useAnalytics } from '@Context/AnalyticsContext';
import { ApexOptions } from 'apexcharts';

const AnalyticsPieChart: React.FC = () => {
  const { modelUsageData, featureUsageData, filters, loading } = useAnalytics();

  type LegendFormatterOpts = {
    seriesIndex: number;
    w: {
      globals: { series: number[] };
    };
  };

  type DonutValueFormatterW = {
    globals: { seriesTotals: number[] };
  };

  const chartData = useMemo(() => {
    if (filters.user || filters.model) {
      const data = featureUsageData.slice(0, 6);
      return {
        title: filters.user
          ? `Feature Distribution for ${filters.user}`
          : `Feature Distribution for ${filters.model?.replace('us.anthropic.', '').replace('amazon.', '')}`,
        subtitle: 'Token usage by feature',
        labels: data.map((item) =>
          item.feature
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase())
        ),
        series: data.map((item) => item.tokens),
        colors: [
          '#8B5CF6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#3B82F6',
          '#6366F1',
        ],
      };
    } else {
      const data = modelUsageData.slice(0, 6);
      return {
        title: 'Model Usage Distribution',
        subtitle: 'Token usage percentage by model',
        labels: data.map((item) =>
          item.model.replace('us.anthropic.', '').replace('amazon.', '')
        ),
        series: data.map((item) => item.tokens),
        colors: [
          '#3B82F6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#8B5CF6',
          '#6366F1',
        ],
      };
    }
  }, [modelUsageData, featureUsageData, filters]);

  const chartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'pie',
        height: 400,
        toolbar: {
          show: false,
        },
      },
      colors: chartData.colors,
      labels: chartData.labels,
      stroke: {
        width: 0,
      },
      fill: {
        type: 'solid',
      },
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 270,
          donut: {
            size: '75%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
              },
              value: {
                show: true,
                fontSize: '14px',
                fontWeight: 400,
                color: '#6B7280',
                formatter: (val: string) => {
                  const numVal = parseInt(val, 10);
                  if (numVal >= 1000000) {
                    return `${(numVal / 1000000).toFixed(1)}M`;
                  } else if (numVal >= 1000) {
                    return `${(numVal / 1000).toFixed(1)}K`;
                  }
                  return numVal.toLocaleString();
                },
              },
              total: {
                show: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                formatter: (w: DonutValueFormatterW) => {
                  const total = w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                  if (total >= 1000000) {
                    return `${(total / 1000000).toFixed(1)}M`;
                  } else if (total >= 1000) {
                    return `${(total / 1000).toFixed(1)}K`;
                  }
                  return total.toLocaleString();
                },
              },
            },
          },
        },
      },
      legend: {
        position: 'right',
        offsetY: 0,
        fontSize: '14px',
        fontWeight: 500,
        formatter: function (seriesName: string, opts: LegendFormatterOpts) {
          const value = opts.w.globals.series[opts.seriesIndex];
          let formattedValue: string;

          if (value >= 1000000) {
            formattedValue = `${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            formattedValue = `${(value / 1000).toFixed(1)}K`;
          } else {
            formattedValue = value.toLocaleString();
          }

          return `${seriesName}: ${formattedValue}`;
        },
      },
      tooltip: {
        y: {
          formatter: (value: number) => {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M tokens`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(1)}K tokens`;
            }
            return `${value.toLocaleString()} tokens`;
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    }),
    [chartData]
  );

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/3 rounded bg-gray-300"></div>
          <div className="mb-4 h-3 w-1/2 rounded bg-gray-200"></div>
          <div className="h-96 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  if (chartData.series.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="py-12 text-center">
          <div className="mb-2 text-lg text-gray-400">No Data Available</div>
          <div className="text-sm text-gray-500">
            No analytics data found for the selected filters.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {chartData.title}
        </h3>
        <p className="text-sm text-gray-600">{chartData.subtitle}</p>
      </div>

      <ReactApexChart
        key={`${chartData.title}-${chartData.series.join(',')}`}
        options={chartOptions}
        series={chartData.series}
        type="donut"
        height={400}
      />
    </div>
  );
};

export default AnalyticsPieChart;
